import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import RouteTracker from "./components/RouteTracker";

// Toast imports
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Eagerly loaded — render before/outside auth (must be instant)
import Login          from './pages/Login';
import SetPassword    from './pages/SetPassword';
import ForgotPassword from './pages/ForgotPassword';
import Header from './components/Header';
import Sidebar from './components/Sidebar';

// Lazy-loaded — only fetched when the user navigates to that route
const Welcome                 = lazy(() => import('./pages/Welcome'));
const SiteManagement          = lazy(() => import('./pages/cookie-governance/SiteManagement'));
const AuditKickoff            = lazy(() => import('./pages/cookie-governance/AuditKickoff'));
const AuditSourceRuleManagement = lazy(() => import('./pages/cookie-governance/AuditSourceRuleManagement'));
const ReportDetails           = lazy(() => import('./pages/cookie-governance/ReportDetails'));
const ReportManagement        = lazy(() => import('./pages/cookie-governance/ReportManagement'));
const CookieSettings          = lazy(() => import('./pages/cookie-governance/CookieSettings'));
const PublishSites            = lazy(() => import('./pages/cookie-governance/PublishSites'));
const CredentialManagement    = lazy(() => import('./pages/external-system/CredentialManagement'));
const ClientManagement        = lazy(() => import('./pages/external-system/ClientManagement'));
const SystemManagement        = lazy(() => import('./pages/external-system/SystemManagement'));
const SystemTypesManagement   = lazy(() => import('./pages/external-system/SystemTypesManagement'));
const RoleManagement          = lazy(() => import('./pages/administration/RoleManagement'));
const PrivilegeManagement     = lazy(() => import('./pages/administration/PrivilegeManagement'));
const ModuleManagement        = lazy(() => import('./pages/administration/ModuleManagement'));
const FeatureManagement       = lazy(() => import('./pages/administration/FeatureManagement'));
const FeaturePrivilegeMapping = lazy(() => import('./pages/administration/FeaturePrivilegeMapping'));
const UserManagement          = lazy(() => import('./pages/administration/UserManagement'));
const OrganizationManagement  = lazy(() => import('./pages/administration/OrganizationManagement'));
const UserProfile             = lazy(() => import('./pages/account/UserProfile'));
const ProductUpdates          = lazy(() => import('./pages/product-updates/ProductUpdates'));


// Authentication Guard
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, authLoading } = useAuth();
    
    if (authLoading) return <></>; // or loader component
    
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }
    
    return (
        <>
            <Header /> 
            {children}
        </>
    );
};

const AppLayout = () => {
    return (
        <div id="main-content-wrapper">
            <Sidebar />
            <main id="main-content">
                <Suspense fallback={<div className="d-flex justify-content-center align-items-center" style={{height:"60vh"}}><div className="spinner-border text-secondary" /></div>}>
                <Routes>
                    <Route path="/" element={<Login />} />
                    <Route path="/welcome" element={<Welcome />} />
                    
                    {/* Cookie Governance Module Routes */}
                    <Route path="/cookie-governance/site-management" element={<SiteManagement />} />
                    <Route path="/cookie-governance/audit-kickoff" element={<AuditKickoff />} />
                    <Route path="/cookie-governance/audit-source-rule-management" element={<AuditSourceRuleManagement />} />
                    <Route path="/cookie-governance/report-management" element={<ReportManagement />} />
                    <Route path="/cookie-governance/reportDetails/:reportId" element={<ReportDetails />} />
                    <Route path="/cookie-governance/settings" element={<CookieSettings />} />
                    <Route path="/cookie-governance/publish-sites" element={<PublishSites />} />

                    {/* External System Module Routes */}
                    <Route path="/external-system/client-management"       element={<ClientManagement />} />
                    <Route path="/external-system/credential-management"   element={<CredentialManagement />} />
                    <Route path="/external-system/system-management"       element={<SystemManagement />} />
                    <Route path="/external-system/system-types-management" element={<SystemTypesManagement />} />

                    {/* Administration Module Routes */}
                    <Route path="/admin/role-management" element={<RoleManagement />} />
                    <Route path="/administration/privilege-management" element={<PrivilegeManagement />} />
                    <Route path="/administration/module-management" element={<ModuleManagement />} />
                    <Route path="/administration/feature-management" element={<FeatureManagement />} />
                    <Route path="/administration/feature-privilege-mapping"  element={<FeaturePrivilegeMapping />} />
                    <Route path="/administration/organization-management" element={<OrganizationManagement />} />
                    <Route path="/admin/user-management" element={<UserManagement />} />

                    {/* Account Routes */}
                    <Route path="/account/profile" element={<UserProfile />} />

                    {/* Product Updates Route */}
                    <Route path="/product-updates" element={<ProductUpdates />} />

                    {/* Fallback for authenticated users */}
                    <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
                </Suspense>
            </main>
        </div>
    );
};


const App = () => {
    return (
        <Router>
            <AuthProvider>
                <RouteTracker />
                <div id="app-container">
                    <Routes>
                        <Route path="/login" element={<Login />} />

                        <Route path="/changepassword/:token" element={<SetPassword />} />
                        <Route path="/forgot-password"      element={<ForgotPassword />} />
                        
                        <Route
                            path="/*"
                            element={
                                <ProtectedRoute>
                                    <AppLayout />
                                </ProtectedRoute>
                            }
                        />
                    </Routes>
                </div>

                <ToastContainer
                    position="top-right"
                    autoClose={3000}
                />
            </AuthProvider>
        </Router>
    );
};


export default App;
